import React, { useState, useEffect, useMemo, createContext, useContext } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LOGO } from "@/assets";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Package, BarChart3, MessageSquarePlus,
  Users, Briefcase, Coins, Ticket, Layers, Receipt, Tag, ScrollText,
  Settings, Menu, LogOut, Activity, Radar, ShoppingCart, Ban, KeyRound, Medal, SlidersHorizontal, FileWarning, MailCheck, Users2, MessagesSquare, ExternalLink, PanelLeftClose, PanelLeftOpen, Award, Shirt, FileCheck, Gauge, Database, FileText, Home, Map, HelpCircle, Camera, DollarSign, ScanSearch, Scale, Handshake, ChevronDown, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HeaderRightCluster, HeaderSecondaryRow } from "@/components/HeaderControls";
import { isAdminRole, isSuperAdmin } from "@/lib/accountRole";
import { useInboxUnread } from "@/context/InboxUnreadContext";
import { AdminGlobalSearch } from "@/components/admin/AdminGlobalSearch";
import { StripeGuardBanner } from "@/components/admin/StripeGuardBanner";

export const SIDEBAR_GROUPS = [
  {
    items: [
      { id: "dashboard", label: "Admin Task Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
      { id: "content-review", label: "Content Review", to: "/admin/content-review", icon: FileWarning, badgeKey: "contentReview", nested: true },
      { id: "inbox", label: "Inbox", to: "/inbox", icon: MessagesSquare, badgeKey: "inbox" },
    ],
  },
  {
    id: "sitrep",
    label: "SITREP",
    items: [
      { id: "sitrep", label: "Platform SitRep", to: "/admin/sitrep", icon: Radar },
      { id: "analytics", label: "Analytics", to: "/admin/analytics", icon: BarChart3 },
      { id: "blocks", label: "Blocks", to: "/admin/blocks", icon: Ban },
      { id: "feedback", label: "Feedback", to: "/admin/feedback", icon: MessageSquarePlus, badgeKey: "feedback" },
    ],
  },
  {
    id: "members",
    label: "MEMBERS",
    items: [
      { id: "listings", label: "Listings", to: "/admin/listings", icon: Package },
      { id: "orders", label: "Orders", to: "/admin/orders", icon: ShoppingCart, badgeKey: "newOrders" },
      { id: "reconciliation", label: "Reconciliation", to: "/admin/reconciliation", icon: Scale },
      { id: "accounts", label: "Users", to: "/admin/accounts", icon: Users },
      { id: "reverify-campaign", label: "Re-verify Campaign", to: "/admin/reverify-campaign", icon: MailCheck },
      { id: "contact-changes", label: "Contact Changes", to: "/admin/contact-changes", icon: KeyRound, badgeKey: "contactChanges" },
      { id: "pending-verification", label: "Pending Verification", to: "/admin/pending-verification", icon: MailCheck, badgeKey: "pendingVerification",
        inlineAction: { label: "Re-verify", to: "/admin/reverify-campaign", testid: "admin-nav-pending-verification-reverify" } },
      { id: "business-applications", label: "Business Applications", to: "/admin/business-applications", icon: Briefcase, badgeKey: "businessApplications" },
      { id: "partner-applications", label: "Partner Applications", to: "/admin/partner-applications", icon: Handshake, badgeKey: "partnerApplications" },
      { id: "licenses", label: "Licenses & Compliance", to: "/admin/licenses", icon: FileCheck, badgeKey: "licenses" },
      { id: "credits", label: "Credits", to: "/admin/credits", icon: Coins },
      { id: "buyer-passes", label: "Buyer Passes", to: "/admin/buyer-passes", icon: Ticket },
      { id: "waitlist", label: "Waitlist", to: "/admin/waitlist", icon: Ticket, badgeKey: "waitlist" },
      { id: "founding", label: "Founding Seats", to: "/admin/founding", icon: Medal },
    ],
  },
  {
    id: "configuration",
    label: "CONFIGURATION",
    items: [
      { id: "platform-config", label: "Platform Config", to: "/admin/platform-config", icon: SlidersHorizontal },
      { id: "tier-permissions", label: "Tier Permissions", to: "/admin/tier-permissions", icon: Layers },
      { id: "membership-cap", label: "Membership Cap", to: "/admin/membership-cap", icon: Gauge },
      { id: "fees", label: "Platform Fees & Boosts", to: "/admin/fees", icon: Receipt },
      { id: "fee-audit", label: "Fee Audit", to: "/admin/fee-audit", icon: ScanSearch },
      { id: "promo-codes", label: "Promo Codes", to: "/admin/promo-codes", icon: Tag },
      { id: "audit-events", label: "Audit Events", to: "/admin/audit-events", icon: ScrollText },
      { id: "test-data", label: "Test-Data Purge", to: "/admin/test-data", icon: Database },
      { id: "settings", label: "Settings", to: "/admin/settings", icon: Settings },
      { id: "view-landing", label: "View Landing Page", href: "/", external: true, icon: ExternalLink },
      { id: "page-swag", label: "OS Swag", to: "/admin/swag", icon: Shirt },
    ],
  },
  {
    id: "website",
    label: "Website Pages",
    items: [
      { id: "page-home", label: "Home / Landing", to: "/admin/pages/home", icon: Home },
      { id: "page-about", label: "About", to: "/admin/pages/about", icon: FileText },
      { id: "page-pricing", label: "Pricing", to: "/admin/pages/pricing", icon: DollarSign },
      { id: "page-map", label: "Map", to: "/admin/pages/map", icon: Map },
      { id: "page-faq", label: "FAQ", to: "/admin/pages/faq", icon: HelpCircle },
      { id: "page-in-the-wild", label: "In The Wild", to: "/admin/pages/in-the-wild", icon: Camera },
      { id: "page-ambassadors", label: "Ambassadors", to: "/admin/ambassadors", icon: Award },
      { id: "page-collaborators", label: "Partners", to: "/admin/collaborators", icon: Handshake },
      { id: "forum", label: "Forum", to: "/admin/forum", icon: MessagesSquare },
    ],
  },
];

export const SIDEBAR_ITEMS = SIDEBAR_GROUPS.flatMap((g) => g.items);

const AdminBadgeContext = createContext({});

// Per-group accordion. Load-time default per page = ONLY the group that owns the current route is
// open; every other collapsible group is collapsed. Admins can still manually toggle any group while
// on the page, but each navigation resets to that "only the current section open" default (no
// persistence across pages, by design).
function useGroupAccordion(activeGroupId, pathname) {
  const [openMap, setOpenMap] = useState({});
  useEffect(() => {
    setOpenMap(activeGroupId ? { [activeGroupId]: true } : {});
  }, [pathname, activeGroupId]);
  const isOpen = (id) => openMap[id] === true;
  const toggle = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  return { isOpen, toggle };
}

function SidebarBrand({ collapsed = false }) {
  return (
    <Link
      to="/feed"
      className={cn("flex flex-col items-center select-none border-b border-sniper", collapsed ? "px-2 py-4" : "px-5 py-5")}
      data-testid="admin-shell-brand"
      aria-label="OpticSwap home"
    >
      <img src={LOGO} alt="OpticSwap" className={cn("w-auto", collapsed ? "h-7" : "h-9")} />
      {!collapsed && (
        <span className="mt-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-olive-bright">
          Admin Portal
        </span>
      )}
    </Link>
  );
}

function SidebarNavBody({ pathname, onItemClick, mobile = false, collapsed = false }) {
  const badges = useContext(AdminBadgeContext);
  const activeGroupId = useMemo(() => {
    for (const g of SIDEBAR_GROUPS) {
      if (!g.id) continue;
      if (g.items.some((it) => !it.external && (pathname === it.to || pathname.startsWith(`${it.to}/`)))) return g.id;
    }
    return null;
  }, [pathname]);
  const { isOpen, toggle } = useGroupAccordion(activeGroupId, pathname);

  const renderItem = (it) => {
    const Icon = it.icon;
    const isActive = !it.external && (pathname === it.to || pathname.startsWith(`${it.to}/`));
    const count = it.badgeKey ? (badges[it.badgeKey] || 0) : 0;
    const cls = cn(
      "group relative flex w-full items-center border-l-2 font-mono uppercase tracking-wide transition-colors text-xs",
      collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-4 py-2.5",
      it.nested && !collapsed && "pl-9",
      mobile && "py-3 text-sm",
      isActive
        ? "border-olive-bright bg-olive/20 text-foreground"
        : "border-transparent text-secondary-foreground hover:bg-white/5 hover:text-foreground"
    );
    const iconEl = (
      <Icon
        className={cn("h-4 w-4 shrink-0 transition-colors", isActive ? "text-olive-bright" : "group-hover:text-foreground")}
        strokeWidth={1.8}
        aria-hidden="true"
      />
    );
    if (it.external) {
      return (
        <a
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onItemClick}
          className={cls}
          data-testid={`admin-nav-${it.id}`}
          title={collapsed ? it.label : undefined}
        >
          {iconEl}
          {!collapsed && <span className="truncate">{it.label}</span>}
          {!collapsed && <ExternalLink className="ml-auto h-3 w-3 shrink-0 text-muted-foreground group-hover:text-foreground" aria-hidden="true" />}
        </a>
      );
    }
    const navEl = (
      <NavLink
        to={it.to}
        onClick={onItemClick}
        className={cn(cls, it.inlineAction && !collapsed && "flex-1 min-w-0")}
        data-testid={`admin-nav-${it.id}`}
        data-active={isActive || undefined}
        aria-current={isActive ? "page" : undefined}
        title={collapsed ? it.label : undefined}
      >
        {iconEl}
        {!collapsed && <span className="truncate">{it.label}</span>}
        {!collapsed && count > 0 && (
          <span
            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-olive text-white font-mono text-[10px] font-bold rounded-full"
            data-testid={`admin-nav-badge-${it.id}`}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
        {collapsed && count > 0 && (
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-olive" data-testid={`admin-nav-badge-${it.id}`} aria-label={`${count} pending`} />
        )}
      </NavLink>
    );
    // Inline secondary action (e.g. Re-verify beside Pending Verification) — a sibling link, never
    // nested inside the NavLink, so it's valid + independently clickable. Hidden when rail-collapsed.
    if (it.inlineAction && !collapsed) {
      return (
        <div className="flex items-stretch">
          {navEl}
          <Link
            to={it.inlineAction.to}
            onClick={onItemClick}
            data-testid={it.inlineAction.testid}
            title={it.inlineAction.label}
            className="shrink-0 inline-flex items-center px-2 my-1 mr-2 border border-olive/50 text-olive-bright hover:bg-olive/20 font-mono uppercase text-[9px] tracking-wide transition-colors"
          >
            {it.inlineAction.label}
          </Link>
        </div>
      );
    }
    return navEl;
  };

  return (
    <div className="flex flex-col gap-4">
      {SIDEBAR_GROUPS.map((group, gi) => {
        const rollup = group.items.reduce((s, it) => s + (it.badgeKey ? (badges[it.badgeKey] || 0) : 0), 0);
        const collapsible = !!group.id && !collapsed; // accordion only in the expanded (240px) rail
        const open = collapsible ? isOpen(group.id) : true;
        return (
          <div key={group.id || group.label || `group-${gi}`}>
            {group.label && !collapsed && (
              collapsible ? (
                <button
                  type="button"
                  onClick={() => toggle(group.id)}
                  aria-expanded={open}
                  data-testid={`admin-nav-group-${group.id}`}
                  className="w-full flex items-center gap-1.5 px-4 pb-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {open ? <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2} /> : <ChevronRight className="h-3 w-3 shrink-0" strokeWidth={2} />}
                  <span>{group.label}</span>
                  {!open && rollup > 0 && (
                    <span
                      className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 bg-[#C2600F] text-white font-mono text-[10px] font-bold rounded-full"
                      data-testid={`admin-nav-group-badge-${group.id}`}
                      aria-label={`${rollup} outstanding`}
                    >
                      {rollup > 99 ? "99+" : rollup}
                    </span>
                  )}
                </button>
              ) : (
                <p className="px-4 pb-1 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {group.label}
                </p>
              )
            )}
            {collapsed && gi > 0 && <div className="mx-3 mb-1 border-t border-sniper" aria-hidden="true" />}
            {open && (
              <ul className="flex flex-col gap-0.5">
                {group.items.map((it) => (
                  <li key={it.id}>{renderItem(it)}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AdminSidebar({ collapsed, onToggle }) {
  const { pathname } = useLocation();
  const { user, adminMode } = useAuth();
  // 3px inset left edge down the sidebar, SAME colour as the mode band (super orange / admin red),
  // only while the band is up. Ties the rail to the band without the Level-3 header tint.
  const bandOn = isAdminRole(user?.role) && adminMode === "admin";
  const edgeColor = bandOn ? (isSuperAdmin(user?.role) ? "#C2600F" : "#A33D28") : undefined;
  return (
    <aside
      className={cn("hidden md:flex shrink-0 bg-surface border-r border-sniper flex-col transition-[width] duration-200", collapsed ? "w-[64px]" : "w-[240px]")}
      style={edgeColor ? { borderLeft: `3px solid ${edgeColor}` } : undefined}
      data-testid="admin-shell-sidebar"
      data-collapsed={collapsed || undefined}
      aria-label="Admin navigation"
    >
      <SidebarBrand collapsed={collapsed} />
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden" role="navigation">
        <SidebarNavBody pathname={pathname} collapsed={collapsed} />
      </nav>
      <button
        onClick={onToggle}
        data-testid="admin-sidebar-toggle"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          "border-t border-sniper flex items-center gap-2 text-secondary-foreground hover:text-foreground hover:bg-white/5 font-mono uppercase text-[10px] tracking-wide transition-colors py-3",
          collapsed ? "justify-center px-0" : "px-4"
        )}
      >
        {collapsed ? <PanelLeftOpen className="h-4 w-4" strokeWidth={1.8} /> : (<><PanelLeftClose className="h-4 w-4" strokeWidth={1.8} /> Collapse</>)}
      </button>
    </aside>
  );
}

function AdminTopBar() {
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const active = SIDEBAR_ITEMS.find((it) => pathname === it.to || pathname.startsWith(`${it.to}/`));
  const title = active?.label || "Admin";

  return (
    <>
      <header
        className="flex items-center justify-between bg-obsidian px-4 md:px-8 py-4"
        data-testid="admin-shell-topbar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center text-foreground"
                style={{ width: 40, height: 40 }}
                aria-label="Open admin menu"
                data-testid="admin-shell-hamburger"
              >
                <Menu size={22} aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 bg-surface border-sniper flex flex-col" data-testid="admin-shell-mobile-sheet">
              <SidebarBrand />
              <nav className="py-3 overflow-y-auto flex-1" role="navigation" data-testid="admin-shell-mobile-nav">
                <SidebarNavBody pathname={pathname} onItemClick={() => setDrawerOpen(false)} mobile />
              </nav>
              <button
                onClick={() => { setDrawerOpen(false); logout(); }}
                data-testid="admin-mobile-logout"
                className="border-t border-sniper flex items-center gap-2 px-4 py-3 text-secondary-foreground hover:text-blaze font-mono uppercase text-sm transition-colors"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.8} /> Log out
              </button>
            </SheetContent>
          </Sheet>
          <h1 className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground truncate" data-testid="admin-shell-heading">
            {title}
          </h1>
        </div>
        <div className="hidden md:flex flex-1 justify-center px-6">
          <AdminGlobalSearch />
        </div>
        {/* Shared authed cluster — BROWSE MARKETPLACE + ADMIN + avatar (Sign out lives in the avatar
            menu; no standalone Log out next to Post). Same component the member header uses. */}
        <div className="hidden xl:flex items-center shrink-0">
          <HeaderRightCluster />
        </div>
      </header>
      {/* Shared second row — + POST · feedback · messages · orders, right-aligned, exactly as the member header. */}
      <HeaderSecondaryRow />
    </>
  );
}

export default function AdminShell({ children }) {
  const [badges, setBadges] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("os_admin_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("os_admin_sidebar_collapsed", next ? "1" : "0"); } catch {}
    return next;
  });
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/admin/contact-changes/pending-count")
        .then(({ data }) => { if (alive) setBadges((b) => ({ ...b, contactChanges: data.count || 0 })); })
        .catch(() => {});
      Promise.all([
        api.get("/admin/content-review?status=open").then(({ data }) => (data.items || []).length).catch(() => 0),
        api.get("/admin/reports?status=open").then(({ data }) => (data.reports || []).length).catch(() => 0),
      ]).then(([screen, reports]) => { if (alive) setBadges((b) => ({ ...b, contentReview: screen + reports })); });
      api.get("/admin/pending-users/counts")
        .then(({ data }) => { if (alive) setBadges((b) => ({ ...b, pendingVerification: data.individual || 0, businessApplications: data.business || 0 })); })
        .catch(() => {});
      api.get("/admin/orders/counts")
        .then(({ data }) => { if (alive) setBadges((b) => ({ ...b, payoutHeld: (data.needs_action != null ? data.needs_action : (data.payout_held || 0)), newOrders: (data.new_orders || 0) })); })
        .catch(() => {});
      api.get("/admin/licenses?status=pending_review")
        .then(({ data }) => { if (alive) setBadges((b) => ({ ...b, licenses: (data.licenses || []).length })); })
        .catch(() => {});
      api.get("/admin/nav-counts")
        .then(({ data }) => { if (alive) setBadges((b) => ({ ...b, feedback: data.feedback || 0, waitlist: data.waitlist || 0, partnerApplications: data.partnerApplications || 0 })); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [pathname]);

  // Inbox unread reads the app-wide InboxUnread context (the SAME source the header + mobile use),
  // which polls while visible and refetches on foreground — so the sidebar badge stays live and
  // agrees everywhere, instead of the old slow 60s admin-only poll.
  const { total: inboxUnread } = useInboxUnread();

  return (
    <AdminBadgeContext.Provider value={{ ...badges, inbox: inboxUnread }}>
      <div className="h-full flex flex-col overflow-hidden" data-testid="admin-shell">
        <div className="flex-1 flex min-h-0">
          <AdminSidebar collapsed={collapsed} onToggle={toggleSidebar} />
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <AdminTopBar />
            <StripeGuardBanner />
            <main className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden min-h-0" data-testid="admin-shell-main">
              <ErrorBoundary>
                {children ? children : (
                  <div className="w-full px-6 py-6" data-testid="admin-content">
                    <Outlet />
                  </div>
                )}
              </ErrorBoundary>
            </main>
          </div>
        </div>
      </div>
    </AdminBadgeContext.Provider>
  );
}
